import React, { type FC, useEffect, useState } from 'react'
import { Box, type SxProps, type Theme } from '@mui/material'

import type { ReadonlyNonEmptyArray } from '../../../../utils'

import type { BufferType } from '../../../../domain'

import type { BaseCommonProps } from '../Base'
import { FormattedBuffer } from './FormattedBuffer'
import { BufferTypeSelect } from './Select'

const wrapperStyle: SxProps<Theme> = { display: 'flex' }
const columBaseStyle: SxProps<Theme> = { m: 'auto 0', fontSize: 'body2.fontSize' }
const columnStyle: SxProps<Theme> = { ...columBaseStyle, ml: 0.5 }
const columnFullWidthStyle: SxProps<Theme> = { ...columnStyle, ml: 'auto' }

type DisplayProps = Readonly<{
    fullWidth?: boolean
    minimized?: boolean

    buffer: Buffer
    types: ReadonlyNonEmptyArray<BufferType>

    onChange?: (value: BufferType) => void
}> &
    Omit<BaseCommonProps, 'id'>

export const Display: FC<DisplayProps> = ({ buffer, types, fullWidth = false, minimized, onChange, sx, className, ...props }) => {
    const [type, setType] = useState(() => types[0])

    useEffect(() => {
        setType(types[0])
    }, [[...types].sort().join(' ')])

    /** If the new types do not include the selected type, then don't render anything */
    if (!types.includes(type)) {
        return null
    }

    const column = fullWidth ? columnFullWidthStyle : columnStyle

    return (
        <Box {...props} className={className} sx={{ ...wrapperStyle, ...sx }}>
            <FormattedBuffer sx={columBaseStyle} buffer={buffer} type={type} minimized={minimized} />
            <BufferTypeSelect
                sx={column}
                value={type}
                types={types}
                onChange={(type) => {
                    setType(type)
                    onChange?.(type)
                }}
            />
        </Box>
    )
}
